import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

const GiveRoundModal = ({
  isOpen,
  onClose,
  member,
  activeGift,
  memberReceivedGift,
  onGiftSuccess,
  initialGiftType,
}) => {
  const [giftType, setGiftType] = useState(initialGiftType || 'next_round');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setGiftType(initialGiftType || 'next_round');
      setMessage('');
      setError(null);
    }
  }, [isOpen, initialGiftType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.giveRound(giftType, message);
      console.log('Give round erfolgreich:', response.data);
      onGiftSuccess(response.data.gift);
      onClose();
    } catch (error) {
      console.error('Give round Fehler:', error);
      setError(error.response?.data?.error || 'Fehler beim Spendieren der Runde');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndGift = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiService.endRound();
      console.log('Gift beendet');
      // Aktive Spende entfernen
      onGiftSuccess(null);
      onClose();
    } catch (error) {
      console.error('End gift Fehler:', error);
      setError(error.response?.data?.error || 'Fehler beim Beenden des Geschenks');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🍺</div>
          <h2 className="text-2xl font-bold text-wood-800 mb-2">Auf meinen Nacken</h2>
          <p className="text-wood-600 text-sm">Spendiere eine Runde für alle</p>
        </div>
        
        {/* Aktive Spende anzeigen */}
        {activeGift && !(activeGift.type === 'next_round' && memberReceivedGift) ? (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-beer-50 to-wood-50 border-2 border-beer-200 rounded-xl p-4">
              <div className="flex items-center mb-3">
                <div className="text-2xl mr-3">🎁</div>
                <h3 className="text-lg font-semibold text-wood-800">Aktives Geschenk</h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-wood-600 font-medium">Typ:</span>
                  <span className="text-wood-800 font-semibold">
                    {activeGift.type === 'next_round' ? 'Nächste Runde' : 'Ganzer Abend'}
                  </span>
                </div>
                {activeGift.message && (
                  <div className="flex justify-between">
                    <span className="text-wood-600 font-medium">Nachricht:</span>
                    <span className="text-wood-800 italic">"{activeGift.message}"</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-wood-600 font-medium">Von:</span>
                  <span className="text-wood-800 font-semibold">{activeGift.giver_name}</span>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-wood-200 hover:bg-wood-300 text-wood-800 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Schließen
              </button>
              {/* Nur Spender selbst oder Admin kann beenden */}
              {(member?.user_username === activeGift.giver_username || member?.user_is_superuser) && (
                <button
                  type="button"
                  onClick={handleEndGift}
                  disabled={isLoading}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Beende...' : 'Geschenk beenden'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-wood-700 mb-3">
                Geschenk-Typ auswählen
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="relative">
                  <input
                    type="radio"
                    value="next_round"
                    checked={giftType === 'next_round'}
                    onChange={(e) => setGiftType(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    giftType === 'next_round'
                      ? 'border-beer-500 bg-beer-50 shadow-lg'
                      : 'border-wood-200 bg-white hover:border-wood-300'
                  }`}>
                    <div className="text-center">
                      <div className="text-2xl mb-2">🍺</div>
                      <div className="font-semibold text-wood-800">Nächste Runde</div>
                      <div className="text-xs text-wood-600 mt-1">Nur das nächste Getränk</div>
                    </div>
                  </div>
                </label>
                
                <label className="relative">
                  <input
                    type="radio"
                    value="full_evening"
                    checked={giftType === 'full_evening'}
                    onChange={(e) => setGiftType(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    giftType === 'full_evening'
                      ? 'border-beer-500 bg-beer-50 shadow-lg'
                      : 'border-wood-200 bg-white hover:border-wood-300'
                  }`}>
                    <div className="text-center">
                      <div className="text-2xl mb-2">🎉</div>
                      <div className="font-semibold text-wood-800">Ganzer Abend</div>
                      <div className="text-xs text-wood-600 mt-1">Alle Getränke heute</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-wood-700 mb-2">
                Nachricht (optional)
              </label>
              <textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-3 py-2 border border-wood-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-beer-500 focus:border-transparent resize-none"
                rows="3"
                placeholder="z.B. 'Prost auf den neuen Job!'"
              />
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-wood-200 hover:bg-wood-300 text-wood-800 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Spende...
                  </div>
                ) : (
                  'Runde spendieren'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default GiveRoundModal;
